import { useState, useCallback, type FormEvent } from "react";
import {
  positions,
  dataAccessOptions,
  positionColorOptions,
  moduleSections,
  nonApplicablePermissions,
  createEmptyPermissions,
  type Position,
  type DataAccessLevel,
  type PositionModuleKey,
  type ModulePermission,
} from "@/mocks/positionsData";

type Props = {
  mode: "add" | "edit";
  position: Position | null;
  onClose: () => void;
  onSave: (data: Omit<Position, "id" | "createdAt" | "userCount">, id?: string) => void;
};

export default function AddEditPositionModal({ mode, position, onClose, onSave }: Props) {
  const isEdit = mode === "edit";

  const [name, setName] = useState(position?.name || "");
  const [level, setLevel] = useState<number>(position?.level || 1);
  const [reportsTo, setReportsTo] = useState(position?.reportsTo || "");
  const [color, setColor] = useState(position?.color || "#4f46e5");
  const [dataAccess, setDataAccess] = useState<DataAccessLevel>(position?.dataAccess || "own");
  const [permissions, setPermissions] = useState<Record<PositionModuleKey, ModulePermission>>(
    position?.permissions ? { ...position.permissions } : createEmptyPermissions()
  );

  const handlePermissionChange = useCallback((module: PositionModuleKey, field: "view" | "create" | "edit" | "delete_", checked: boolean) => {
    setPermissions((prev) => {
      const current = { ...prev[module] };
      if (field === "view" && !checked) {
        current.view = false;
        current.create = false;
        current.edit = false;
        current.delete_ = false;
      } else {
        current[field] = checked;
        if (checked && field !== "view") {
          current.view = true;
        }
      }
      return { ...prev, [module]: current };
    });
  }, []);

  const handleSectionSelectAll = useCallback((modules: PositionModuleKey[], checked: boolean) => {
    setPermissions((prev) => {
      const next = { ...prev };
      modules.forEach((m) => {
        const p = { ...next[m] };
        p.view = checked;
        const na = nonApplicablePermissions[m] || [];
        p.create = checked && !na.includes("create");
        p.edit = checked && !na.includes("edit");
        p.delete_ = checked && !na.includes("delete_");
        next[m] = p;
      });
      return next;
    });
  }, []);

  const isSectionAllChecked = useCallback((modules: PositionModuleKey[]): boolean => {
    return modules.every((m) => {
      const p = permissions[m];
      const na = nonApplicablePermissions[m] || [];
      const allApplicable = (["view", "create", "edit", "delete_"] as const).every((f) => {
        if (na.includes(f)) return true;
        return p[f] === true;
      });
      return allApplicable;
    });
  }, [permissions]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSave(
      {
        name: name.trim(),
        level,
        reportsTo,
        color,
        dataAccess,
        permissions: { ...permissions },
      },
      position?.id
    );
  };

  const reportsToOptions = positions.filter((p) => p.id !== position?.id);

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto">
        <div className="bg-white rounded-xl w-full max-w-3xl shadow-2xl my-8">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
            <h2 className="text-lg font-bold text-gray-900">
              {isEdit ? `Edit ${position?.name}` : "Add New Position"}
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <i className="ri-close-line text-xl w-5 h-5 flex items-center justify-center" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="max-h-[75vh] overflow-y-auto">
            {/* SECTION 1: Basic Info */}
            <div className="px-6 py-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Position Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Regional Manager, Team Leader, Sales Agent"
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Level <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    value={level}
                    onChange={(e) => setLevel(Number(e.target.value))}
                    className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                  />
                  <p className="text-xs text-gray-400 mt-0.5">1 = highest authority (Owner), higher number = lower in hierarchy</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Reports To <span className="text-red-500">*</span>
                </label>
                <select
                  value={reportsTo}
                  onChange={(e) => setReportsTo(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-900 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-200 transition-all"
                >
                  <option value="">— Top Level (no manager)</option>
                  {reportsToOptions.map((p) => (
                    <option key={p.id} value={p.name}>{p.name}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-0.5">Who does this position report to?</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Position Color</label>
                <div className="flex items-center gap-2">
                  {positionColorOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setColor(opt.value)}
                      className={`w-8 h-8 rounded-full border-2 transition-all cursor-pointer ${
                        color === opt.value ? "border-gray-900 scale-110" : "border-transparent hover:scale-105"
                      }`}
                      style={{ backgroundColor: opt.value }}
                      title={opt.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* SECTION 2: Data Access */}
            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Data Visibility</h3>
              <p className="text-sm text-gray-500 mb-4">What data can people in this position see?</p>

              <div className="space-y-2">
                {dataAccessOptions.map((opt) => (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-3.5 rounded-xl border cursor-pointer transition-all ${
                      dataAccess === opt.value
                        ? "border-2 border-indigo-600 bg-indigo-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <input
                      type="radio"
                      name="dataAccess"
                      value={opt.value}
                      checked={dataAccess === opt.value}
                      onChange={() => setDataAccess(opt.value)}
                      className="mt-0.5 accent-indigo-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{opt.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{opt.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* SECTION 3: Module Permissions */}
            <div className="border-t border-gray-200 px-6 py-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-1">Module Permissions</h3>
              <p className="text-sm text-gray-500 mb-4">Define what this position can do in each module</p>

              <div className="border border-gray-200 rounded-xl overflow-hidden">
                {/* Column headers */}
                <div className="grid grid-cols-[1fr_repeat(4,56px)] gap-0 bg-gray-50 border-b border-gray-200 px-4 py-2.5">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase">Module</span>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase text-center">View</span>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase text-center">Create</span>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase text-center">Edit</span>
                  <span className="text-[11px] font-semibold text-gray-500 uppercase text-center">Delete</span>
                </div>

                {moduleSections.map((section) => {
                  const allChecked = isSectionAllChecked(section.modules);
                  return (
                    <div key={section.name}>
                      {/* Section header */}
                      <div className="grid grid-cols-[1fr_repeat(4,56px)] gap-0 bg-gray-50/50 border-b border-gray-100 px-4 py-2">
                        <span className="text-xs font-semibold text-gray-700">{section.name}</span>
                        <div className="col-span-4 flex justify-end">
                          <button
                            type="button"
                            onClick={() => handleSectionSelectAll(section.modules, !allChecked)}
                            className="text-[11px] text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap"
                          >
                            {allChecked ? "Deselect All" : "Select All"}
                          </button>
                        </div>
                      </div>

                      {section.modules.map((mod) => {
                        const p = permissions[mod];
                        const na = nonApplicablePermissions[mod] || [];
                        return (
                          <div
                            key={mod}
                            className="grid grid-cols-[1fr_repeat(4,56px)] gap-0 border-b border-gray-50 px-4 py-2 items-center hover:bg-gray-50/50"
                          >
                            <span className="text-xs text-gray-700">{mod}</span>
                            {(["view", "create", "edit", "delete_"] as const).map((field) => {
                              if (na.includes(field)) {
                                return (
                                  <span key={field} className="text-center text-[11px] text-gray-300">—</span>
                                );
                              }
                              return (
                                <div key={field} className="flex justify-center">
                                  <input
                                    type="checkbox"
                                    checked={p[field]}
                                    onChange={(e) => handlePermissionChange(mod, field, e.target.checked)}
                                    className="w-3.5 h-3.5 accent-indigo-600 rounded"
                                  />
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center gap-2.5 px-6 py-4 border-t border-gray-200 justify-end sticky bottom-0 bg-white rounded-b-xl">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-sm font-medium text-white transition-colors whitespace-nowrap"
              >
                {isEdit ? "Update Position" : "Save Position"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}